/**
 * Helper component for rendering basic text inputs
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';

// import third-party libraries
import _ from 'lodash';
import classNames from 'classnames';

// import components
import Base from "../BaseComponent.js.jsx";
import VideoPlayer from '../helpers/VideoPlayer.js.jsx';
import PDFViewer from '../../../global/components/helpers/PDFViewer.js.jsx';

import videoUtils from '../../utils/videoUtils';

class CdnPathInput extends Base {
  constructor(props) {
    super(props);
    this.state = {
      copyPathButtonStyle: {
        className: 'far fa-clipboard'
      }
      , errorMessage: null
      , extention: null
      , isFocused: false
      , path:  this.props.value
      , playerId: Math.floor(Math.random()*100000) + '_media-preview'
      , prefix: 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/visits/'
      /**
       * NOTE: We don't want to show them the cloudfront URL in Authoring since they can't access this 
       * directly anyway.  They *should* just post the S3 path. Cloudfront will automatically switch it.
       * 
       * cloudfront prefix: 'https://d2jth2langi9vb.cloudfront.net/visits/'
       */
      , type: null
    }
    this._bind(
      '_getExtension'
      , '_getType'
      , '_handleCopyPathToClipboard'
      , '_handleInputChange'
      , '_setFocus'
      , '_toggleFocus'
    );
  }

  componentDidMount() {
    let extension = this._getExtension(this.props.value);
    let type = this._getType(extension);
    this.setState({
      extension
      , type
    })
  }

  componentWillReceiveProps(nextProps) {
    if(nextProps.value !== this.props.value) {
      let extension = this._getExtension(nextProps.value);
      let type = this._getType(extension);
      this.setState({
        extension
        , path: nextProps.value
        , type
      })
    }
  }

  _setFocus() {
    this.nameInput.focus();
  }

  _toggleFocus() {
    this.setState({isFocused: !this.state.isFocused});
  }

  _handleCopyPathToClipboard() {
    const { prefix, path } = this.state
    /*
     * NOTE: This works in Chrome. This has NOT been tested in Firefox because Firefox requires HTTPS to access the clipboard.
     */
    // get permission to access the keyboard
    navigator.permissions.query({name: "clipboard-write"}).then(result => {
      if(result.state === "granted" || result.state === "prompt") {
        // build the complete video path.
        let completePath = prefix;
        completePath += path;
        // write the completePath to the clipboard
        navigator.clipboard.writeText(completePath).then(() => {
          // if successful, change the icon to notify user.
          this.setState({
            copyPathButtonStyle: {
              className: 'far fa-clipboard-check'
              , style: {
                color: 'green'
              }
            }
          }, () => {
            // change the icon back to normal after 1 second.
            setTimeout(() => {
              this.setState({
                copyPathButtonStyle: {
                  className: 'far fa-clipboard'
                }
              });
            }, 1000)
          });
        });
        return;
      } else {
        // if unsuccessful, change the icon to notify user.
        this.setState({
          copyPathButtonStyle: {
            className: 'far fa-clipboard'
            , style: {
              color: 'red'
            }
          }
        }, () => {
          // change the icon back to normal after 1 second.
          setTimeout(() => {
            this.setState({
              copyPathButtonStyle: {
                className: 'far fa-clipboard'
              }
            });
          }, 1000)
        });
        return;
      }
    });
    // if the permissons query caused an error we'll end up here. Change the icon to notify user.
    this.setState({
      copyPathButtonStyle: {
        className: 'far fa-clipboard'
        , style: {
          color: 'red'
        }
      }
    }, () => {
      // change the icon back to normal after 1 second.
      setTimeout(() => {
        this.setState({
          copyPathButtonStyle: {
            className: 'far fa-clipboard'
          }
        });
      }, 1000)
    });
  }

  _handleInputChange(e) {
    let newState = _.update( this.state, e.target.name, function() {
      return e.target.value;
    });

    newState.extension = this._getExtension(newState.path);
    newState.type = this._getType(newState.extension);
    var event = {
      target: {
        name: this.props.name
      }
    };
    newState.errorMessage = null;
    if(this.props.allowType && newState.type && newState.type !== this.props.allowType) {
      newState.errorMessage = `Wrong file type. Expected ${this.props.allowType}, recieved ${newState.type}. Please try again.`
      alert(newState.errorMessage)
      newState.path = ''
      event.target.value = newState.path
      this.props.change(event);
    } else if(newState.type) {
      event.target.value = newState.path; // return valid path
      // only tell parent about a change event if the new value is valid
      this.props.change(event);
    } else if(!newState.type && newState.extension) {
      newState.errorMessage = "not supported";
    } else if(!newState.path) {
      event.target.value = '';
      this.props.change(event);
    }
    this.setState(newState);
  }

  _getExtension(path) {
    let extIndex = path ? path.lastIndexOf('.') : null;
    // console.log('extIndex', extIndex);
    // console.log(path.substring(extIndex + 1));
    return extIndex ? path.substring(extIndex + 1) : "";
  }

  _getType(extension) {
    // console.log('getType for', extension)
    const videoTypes = ['mp4', 'webm'];
    const imageTypes = ['png', 'jpg', 'jpeg', 'gif'];
    const documentTypes = ['pdf'];
    if(videoTypes.indexOf(extension) > -1) {
      return 'video';
    } else if(imageTypes.indexOf(extension) > -1) {
      return 'image';
    } else if(documentTypes.indexOf(extension) > -1) {
      return 'document';
    } else {
      return null;
    }
  }

  render() {
    const {
      disabled
      , helpText
      , label
      , name
      , required
      , value
    } = this.props;
    const {
      copyPathButtonStyle
      , errorMessage
      , extension
      , isFocused
      , path
      , playerId
      , prefix
      , type
    } = this.state;
    let prefixClass = classNames(
      'item -prefix'
      , { '-focused': isFocused }
    )
    // showCopyPath is set to true by default.
    const showCopyPath = this.props.showCopyPath === false ? false : true;
    let copyPathAddOn = showCopyPath ? <span className="item yt-btn link" onClick={this._handleCopyPathToClipboard}><i {...copyPathButtonStyle}></i></span> : null;

    return (
      <div className="input-group -cdn-input">
        <label htmlFor={name}> {label} {required ? <sup className="-required">*</sup> : null}</label>
        <div className="input-add-on ">
          <span className={prefixClass} onClick={this._setFocus}>
            {prefix}
          </span>
          <input
            className="field"
            disabled={disabled}
            name="path"
            onBlur={this._toggleFocus}
            onChange={this._handleInputChange}
            onFocus={this._toggleFocus}
            ref={(input) => { this.nameInput = input; }}
            required={required}
            type="text"
            value={path}
          />
          {copyPathAddOn}
        </div>
        <small className="help-text"><em>i.e. rs-test-vids/4.webm</em></small>
        <div className="-media-preview">
          <small><em>{label} preview</em></small>
          { this.state.errorMessage ?
            <div>Error</div>
            :
            type === 'image' ?
            <img src={prefix + path} />
            : type === 'video' ?
            <VideoPlayer
              isAutoPlay={false}
              playerId={playerId}
              playlist={videoUtils.returnPlaylist(path, null, null)}
            />
            : type === 'document' ?
            <PDFViewer
              file={path}
            />
            : extension ?
            <em><strong>Not supported</strong></em>
            :
            null
          }
        </div>
        <small className="help-text"><em>{helpText}</em></small>
      </div>
    )
  }
}

CdnPathInput.propTypes = {
  allowType: PropTypes.oneOf(['document', 'image', 'video'])
  , change: PropTypes.func.isRequired
  , showCopyPath: PropTypes.bool
  , disabled: PropTypes.bool
  , helpText: PropTypes.any
  , label: PropTypes.string
  , name: PropTypes.string.isRequired
  , required: PropTypes.bool
  , value: PropTypes.string.isRequired
}

CdnPathInput.defaultProps = {
  disabled: false
  , helpText: null
  , label: ''
  , required: false
  , value: ''
}

export default CdnPathInput;



// WEBPACK FOOTER //
// global/components/forms/CdnPathInput.js.jsx